import { useTranslation } from "react-i18next";
import Button from "./ui/Button";
import UserModel from "../models/UserModel";
import { useEffect, useState } from "react";
import { userServiceFactory } from "../services/UserServiceImpl";
import { UpdateUserDTO } from "../domain/models/dto/UpdateUserDTO";
import useCustomFormik from "../custom-hooks/useCustomFormik";
import { updateUserSchemaFields, updateUserSchemaFactory } from "../validation-schemas/UpdateUserSchema";
import StarRatingSVG from "../custom-components/svg/functional-icons/StarRatingSVG";
import IconChevronUpSVG from "../custom-components/svg/functional-icons/IconChevronUpSVG";
import { ButtonVariant } from "../types/constants";
import { ImageUploader } from "./shared/ImageUploader";
import { Purpose } from "../domain/enums/Purpose";
import _ from "lodash";
import { Owner } from "../domain/enums/Owner";
import { useFileEncoder } from "./shared/form-inputs/useFileEncoder";

interface EditAccountDataProps {
    user: UserModel;
    initialOpen?: boolean;
    onClose: () => void;
    onSave?: (savedUser: any) => void;
}

const buildFilesPayload = async ({
    selectedFile,
    avatarImageUrl,
    user,
    encodeFiles,
}: {
    selectedFile: File | null;
    avatarImageUrl: string | null;
    user: UserModel;
    encodeFiles: (file: File) => Promise<string | string[]>;
}) => {
    if (selectedFile) {
        const encodedData = await encodeFiles(selectedFile);
        const dataUrl = Array.isArray(encodedData) ? encodedData[0] : (encodedData as string);
        const fileExtension = selectedFile.type
            ? selectedFile.type.split('/')[1]
            : (selectedFile.name.split('.').pop() || 'png');

        return [{
            dataUrl,
            purposeType: Purpose.AVATAR,
            ownerType: Owner.USER,
            fileType: fileExtension,
            order: 1,
        }];
    }

    if (avatarImageUrl) {
        const originalFileFromDB = (user as any).files?.find((f: any) => f.purposeType === Purpose.AVATAR) || null;

        if (originalFileFromDB) {
            return [originalFileFromDB];
        }

        return [{
            dataUrl: avatarImageUrl,
            filePath: avatarImageUrl,
            purposeType: Purpose.AVATAR,
            ownerType: Owner.USER,
            fileType: 'png',
            order: 1,
        }];
    }

    return [];
};

export default function EditAccountData({
    user,
    initialOpen = false,
    onClose,
    onSave
}: EditAccountDataProps) {
    const { t } = useTranslation();
    const { encodeFiles } = useFileEncoder();

    const [state, setState] = useState({ open: initialOpen, spinning: false });

    const [isImageBroken, setIsImageBroken] = useState<boolean>(false);

    const [avatarImageUrl, setAvatarImageUrl] = useState<string | null>(
        user?.pictureExists(Purpose.AVATAR) ? user.getPictureUrl() : null
    );

    const [selectedFile, setSelectedFile] = useState<File | null>(null);

    const { form } = useCustomFormik<UpdateUserDTO>({
        initial: {
            firstName: user.firstName,
            lastName: user.lastName,
            middleName: user.middleName,
            phone: user.phone,
            position: user.position,
            description: user.description,
        } as UpdateUserDTO,
        fields: updateUserSchemaFields,
        schema: updateUserSchemaFactory(t),
        onSubmit: async (values) => {
            if (isImageBroken) return;

            try {
                const userService = userServiceFactory();
                const filesPayload = await buildFilesPayload({
                    selectedFile,
                    avatarImageUrl,
                    user,
                    encodeFiles,
                });

                const dto = {
                    id: user.id || (user as any).userId,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    middleName: values.middleName,
                    phone: values.phone,
                    position: values.position,
                    description: values.description,
                    files: filesPayload,
                };

                const savedUser = await userService.updateUser(dto as any);

                if (onSave) {
                    onSave(savedUser || dto);
                }
                onClose();
            } catch (error) {
                console.error(t("account.loadError"), error);
            }
        },
    });

    useEffect(() => {
        if (!selectedFile) {
            setAvatarImageUrl(user?.pictureExists(Purpose.AVATAR) ? user.getPictureUrl() : null);
        }
    }, [user, selectedFile]);

    const handleFileChange = async (files: File[] | null) => {
        if (_.isEmpty(files)) return;

        const file = files[0];

        if (file.size === 0) {
            setSelectedFile(null);
            setAvatarImageUrl(null);
            setIsImageBroken(true);
            return;
        }

        setIsImageBroken(false);
        setSelectedFile(file);
        const localPreviewUrl = URL.createObjectURL(file);
        setAvatarImageUrl(localPreviewUrl);
    };

    const isSaveDisabled = !form.isValid || isImageBroken;

    return (
        <div className="flex flex-col">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between md:flex-row md:justify-between md:items-center gap-9">
                <h1 className="font-geologica text-3xl font-medium uppercase text-primary-400">
                    {t("account.pageTitle")}
                </h1>

                <div className="w-full md:w-[300px] lg:w-[370px] flex flex-col md:flex-row lg:flex-row md:justify-between md:items-center mt-6 md:mt-0 gap-6">
                    <button
                        className="order-2 md:order-1 w-full md:w-[170px] lg:w-[170px] flex items-start justify-start md:h-auto lg:flex-row text-base text-gray-600 uppercase font-semibold hover:text-gray-800 transition-colors ps-4"
                        onClick={(e: any) => {
                            e.preventDefault();
                            onClose();
                        }}
                    >
                        {t("account.buttonText.cancelSaving")}
                    </button>

                    <Button
                        className={`order-1 md:order-2 w-full lg:w-[170px] md:w-full flex items-center justify-start h-[50px] gap-3 px-4 ${
                            isSaveDisabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""
                        }`}
                        text={t("account.buttonText.saveChanges")}
                        variant={ButtonVariant.SAVE}
                        disabled={isSaveDisabled}
                        onClick={(e: any) => {
                            if (e && e.preventDefault) e.preventDefault();
                            if (!isSaveDisabled) {
                                form.handleSubmit();
                            }
                        }}
                    />
                    {isSaveDisabled && (
                        <span className="text-[10px] text-red-500 mt-1 text-center">
                            {isImageBroken
                                ? t("account.errors.fixCorruptedImage", "Виправте пошкоджене фото")
                                : t("validation.checkFields")}
                        </span>
                    )}
                </div>
            </div>

            <div className="relative py-0 md:py-24 lg:py-0 mt-8">
                <div className="max-w-[1280px] mx-auto flex flex-col lap:flex-row gap-12 xl:gap-[60px]">
                    <div className="w-[380px] h-[380px] md:w-[560px] md:h-[560px] lg:w-[620px] lg:h-[620px]
                        bg-background-100 border border-border-400 rounded-md px-6 py-6 md:px-8 md:py-8 flex items-center justify-center text-center gap-2 md:gap-5 shadow-[0px_6px_6px_0px_#36335214] border-2">
                        <div className="text-sm font-medium text-light-text-100 dark:text-dark-text-100">
                            <ImageUploader
                                imageUrl={avatarImageUrl}
                                deleteImage={() => {
                                    setAvatarImageUrl(null);
                                    setSelectedFile(null);
                                    setIsImageBroken(false);
                                }}
                                onChange={(files) => handleFileChange(files as any)}
                                onImageBrokenChange={(broken) => setIsImageBroken(broken)}
                                user={user}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 flex-1">
                        <div className="flex flex-col gap-2">
                            <span className="lg:text-xl text-gray-400">{t("account.accountInformation.lastName")}</span>
                            <input
                                name="lastName"
                                value={form.values.lastName}
                                onChange={form.handleChange}
                                className="w-full md:w-1/5 lg:w-[620px] lg:text-xl text-text-700 border border-3 border-border-300 outline-none focus:border-primary-400 rounded-lg py-2 px-4"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <span className="lg:text-xl text-gray-400">{t("account.accountInformation.firstName")}</span>
                            <input
                                name="firstName"
                                value={form.values.firstName}
                                onChange={form.handleChange}
                                className="w-full md:w-1/5 lg:w-[620px] lg:text-xl text-text-700 border border-3 border-border-300 outline-none focus:border-primary-400 rounded-lg py-2 px-4"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <span className="lg:text-xl text-gray-400">{t("account.accountInformation.middleName")}</span>
                            <input
                                name="middleName"
                                value={form.values.middleName}
                                onChange={form.handleChange}
                                className="w-full md:w-1/5 lg:w-[620px] lg:text-xl text-text-700 border border-3 border-border-300 outline-none focus:border-primary-400 rounded-lg py-2 px-4"
                            />
                        </div>

                        {/* <span className="lg:text-xl text-gray-400">{t("account.accountInformation.rating")}</span>

                        <div className="flex gap-5">
                            <p className="lg:text-xl text-text-700">{(user.rating)}</p>
                            {Array.from({ length: 5 }).map((_, index) => (
                                <StarRatingSVG key={index} />
                            ))}
                        </div> */}

                        <span className="lg:text-xl text-gray-400">
                            {t("account.accountInformation.accountDescription")}
                        </span>
                        <div className="w-full md:w-1/5 lg:w-[620px] flex flex-col rounded-lg overflow-hidden bg-background-100">
                            <div className="overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setState((prev) => ({ ...prev, spinning: true, open: !prev.open }));
                                        window.setTimeout(() => setState((prev) => ({ ...prev, spinning: false })), 300);
                                    }}
                                    className="flex justify-between items-start w-full min-h-[50px] px-0 text-left pt-2 transition-colors duration-300"
                                >
                                    <div className="flex overflow-hidden pr-4 flex-1 items-start">
                                        {!state.open && (
                                            <p className={`lg:text-xl truncate w-full text-left transition-opacity duration-300 ${!form.values.description ? "text-gray-400" : "text-text-700"}`}>
                                                {form.values.description || t("account.accountInformation.descriptionMissing")}
                                            </p>
                                        )}
                                    </div>

                                    <IconChevronUpSVG initialOpen={state.open} spinning={state.spinning} />
                                </button>

                                <div className="overflow-hidden">
                                    <div className={`grid transition-all duration-300 ease-in-out ${state.open ? 'grid-rows-[1fr] opacity-100 ' : 'grid-rows-[0fr] opacity-0'}`}>
                                        <div className="overflow-hidden">
                                            <textarea
                                                name="description"
                                                rows={5}
                                                value={form.values.description}
                                                onChange={form.handleChange}
                                                className="w-full md:w-1/5 lg:w-[620px] lg:text-xl text-text-700 border border-3 border-border-300 outline-none focus:border-primary-400 rounded-lg py-2 px-4"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-2">
                            <span className="lg:text-xl text-gray-400">{t("account.accountInformation.position")}</span>
                            <input
                                name="position"
                                value={form.values.position}
                                onChange={form.handleChange}
                                className="w-full md:w-1/5 lg:w-[620px] lg:text-xl text-text-700 border border-3 border-border-300 outline-none focus:border-primary-400 rounded-lg py-2 px-4"
                            />
                        </div>

                        <div className="flex flex-col gap-2">
                            <span className="lg:text-xl text-gray-400">{t("account.accountInformation.telephone")}</span>
                            <input
                                name="phone"
                                value={form.values.phone}
                                onChange={form.handleChange}
                                className="w-full md:w-1/5 lg:w-[620px] lg:text-xl text-text-700 border border-3 border-border-300 outline-none focus:border-primary-400 rounded-lg py-2 px-4"
                            />
                        </div>

                        <span className="lg:text-xl text-gray-400">{t("account.accountInformation.email")}</span>
                        <p className="lg:text-xl text-text-700">{user.email}</p>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                className="w-4 h-4 accent-blue-600"
                            />
                            <span className="lg:text-xl text-gray-700">
                                {t("account.accountInformation.receiveNewsEmail")}
                            </span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
}
